import React from 'react'
import {
  Box,
  Typography,
  Container,
  Divider,
  Button,
  Paper
} from '@mui/material'
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Star as StarIcon
} from '@mui/icons-material'
import { type RealtorData } from './RealtorCard'
import topImage from '../assets/top.png'

export interface RealtorDetailProps {
  data: RealtorData
  onContact?: (realtor: RealtorData) => void
  onClose?: () => void
}

const RealtorDetail: React.FC<RealtorDetailProps> = ({
  data,
  onContact,
  onClose
}) => {
  const {
    image,
    name,
    title,
    description,
    phone,
    email
  } = data

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        fontFamily: 'Montserrat, sans-serif',
      }}
    >
      <Container maxWidth="lg">
        {/* Main Content */}
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
        >
          {/* Realtor Info Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              padding: { xs: '30px 20px', md: '40px' },
              gap: { xs: 3, md: 4 }
            }}
          >
            {/* Photo - Square/Rectangular */}
            <Box sx={{ flexShrink: 0 }}>
              <Box
                component="img"
                src={image}
                alt={name}
                sx={{
                  width: { xs: 350, md: 300 },
                  height: { xs: 350, md: 300 },
                  objectFit: 'cover',
                  borderRadius: 2
                }}
              />
            </Box>

            {/* Info - Centered */}
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              {/* Decorative element from top.png */}
              <Box sx={{ marginBottom: 3 }}>
                <Box
                  component="img"
                  src={topImage}
                  alt="decorative element"
                  sx={{
                    height: '30px',
                    width: 'auto'
                  }}
                />
              </Box>

              {/* Name and Title */}
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 700,
                  color: '#0e2340',
                  marginBottom: 1
                }}
              >
                {name}
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: '#b08b4f',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: 3
                }}
              >
                {title}
              </Typography>

              {/* Contact Information - Centered container with left-aligned content */}
              <Box sx={{ marginBottom: 3, display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'left' }}>
                  {/* Phone */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                    onClick={() => window.open(`tel:${phone}`, '_self')}
                  >
                    <PhoneIcon sx={{ fontSize: '20px', marginRight: '12px', color: '#0e2340' }} />
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#0e2340',
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }}
                    >
                      {phone}
                    </Typography>
                  </Box>

                  {/* Email */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                    onClick={() => window.open(`mailto:${email}`, '_self')}
                  >
                    <EmailIcon sx={{ fontSize: '20px', marginRight: '12px', color: '#0e2340' }} />
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#0e2340',
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }}
                    >
                      {email}
                    </Typography>
                  </Box>

                  {/* License */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <StarIcon sx={{ fontSize: '20px', marginRight: '12px', color: '#0e2340' }} />
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#0e2340',
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }}
                    >
                      License #: SL3012847
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>


          {/* Biography Section */}
          <Box sx={{ padding: { xs: '30px 20px', md: '40px' } }}>
            <Typography
              variant="body1"
              sx={{
                color: '#0e2340',
                fontSize: '1.1rem',
                lineHeight: 1.8,
                textAlign: 'justify',
                marginBottom: 3
              }}
            >
              {description}
            </Typography>

            {/* Call to Action */}
            <Box sx={{ textAlign: 'center', marginTop: 4 }}>

            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

export default RealtorDetail
